/**
 * Wire TG BackButton to React Router history. The default behaviour of TG's
 * BackButton is to fire an event; we hook into it and call `navigate(-1)`
 * (or a custom handler). Hide automatically on root route.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTg } from './useTelegram';

export function useBackButton(handler?: () => void): void {
  const tg = useTg();
  const navigate = useNavigate();
  const location = useLocation();

  // Stash latest handler in ref so we don't re-register every render
  const handlerRef = useRef<(() => void) | null>(null);
  handlerRef.current = handler ?? null;

  useEffect(() => {
    if (!tg) return;
    const bb = tg.BackButton;
    const isRoot = location.pathname === '/';
    if (isRoot && !handlerRef.current) {
      bb.hide();
      return;
    }
    const onClick = (): void => {
      if (handlerRef.current) handlerRef.current();
      else navigate(-1);
    };
    bb.onClick(onClick);
    bb.show();
    return () => {
      bb.offClick(onClick);
      bb.hide();
    };
  }, [tg, location.pathname, navigate]);
}
